//=============================================================================
//  Copyright : 2021 semantics GmbH
//  Date      : $Date: 2024-12-01 03:39:01 +0100 (Sun, 01 Dec 2024) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 147928 $
//  Purpose   : column support
// =============================================================================
// ---Third-party imports
import React from 'react'
import {Button} from 'semantic-ui-react'
import {matchSorter} from 'match-sorter'
import {parseISO, add, isValid} from 'date-fns'
import clsx from 'clsx'

// ---Framework imports
import {
  get,
  keys,
  isString,
  mapItems,
  has,
  uniq,
  strip,
  dict,
  isArray,
  isSet,
  flatten,
  size,
} from 'ldash'
import {Actions} from 'uix/registries'
import {useState, useDebounce, useEffect, useMemo} from 'uix/hooks'
import log from 'uix/logging'
import {XPopup} from 'uix/components'

// ---Local imports
import styles from './styles.scss'

// ---Exports
export const CATEGORIAL_ALL = 'all'
export const NUMBER_TYPE = 'number'

const textFilter = ({name, title, validate}) => ({filter, onChange}) => {
  let [text, setText] = useState(null)
  let [debounced] = useDebounce(text, 300, {leading: false})
  useEffect(() => {
    if (debounced !== null) {
      onChange(debounced)
    }
  }, [debounced])
  let text_val = text === null ? filter?.value : text
  let is_invalid = validate && !validate(debounced || '')
  return (
    <input
      value={text_val || ''}
      type="text"
      onChange={event => setText(event.target.value)}
      className={clsx(
        styles.colFilter,
        !!text_val && styles.activeFilter,
        is_invalid && styles.invalidFilter
      )}
      title={title}
      name={name}
    />
  )
}

export const filterDates = colid => {
  /* filter by date values with support for:
    - equality with granularities:
      * 2012-07-17 13:11
      * 2012-07-17 13
      * 2012-07-17
      * 2012-07
      * 2012
    - less than / greater than (with above granuarities):
      * < 2012-07-17
      * > 1980
    - ranges (with above granuarities):
      * 1900 -> 2000
      * 2021-03-03 15:31 -> 2021-03-03 16:17:33
  */
  let dt_is_equal = (dt, dt_start, dt_end) => dt >= dt_start && dt <= dt_end
  let dt_is_lesser = (dt, dt_start) => dt < dt_start
  let dt_is_greater = (dt, dt_end) => dt > dt_end
  let count_chars = (s, c) => s.split(c).length - 1
  let get_granularity = (s, dt) => {
    let count_dt_sep = count_chars(s, '-')
    let count_time_sep = count_chars(s, ':')
    let is_time = count_chars(s, ' ') + count_chars(s, 'T') > 0
    // return date-fns add({name: 1}) compatible name
    if (count_dt_sep == 0) return 'years'
    if (count_dt_sep == 1) return 'months'
    if (count_dt_sep == 2 && !is_time) return 'days'
    if (count_time_sep == 0) return 'hours'
    if (count_time_sep == 1) return 'minutes'
    if (count_time_sep == 2) return 'seconds'
  }
  let get_granular_dt_end = (v, dt_start) => {
    let g = get_granularity(v, dt_start)
    if (g) {
      return add(dt_start, {[g]: 1})
    }
    return dt_start
  }
  let decode_filter = filter_val => {
    let f_val = strip(filter_val || '')
    if (!f_val) {
      return {}
    }

    let relation = dt_is_equal
    if (f_val.startsWith('<') || f_val.startsWith('>')) {
      relation = f_val.startsWith('<') ? dt_is_lesser : dt_is_greater
      f_val = strip(f_val.slice(1))
    }
    let [dt_start, dt_end] = [null, null]

    if (f_val.split('->').length == 2) {
      let [v_start, v_end] = f_val.split('->').map(strip)
      dt_start = parseISO(v_start)
      dt_end = parseISO(v_end)
    } else {
      dt_start = parseISO(f_val)
      if (isValid(dt_start)) {
        dt_end = get_granular_dt_end(f_val, dt_start)
      }
    }
    if (!isValid(dt_start)) {
      return {invalid: true}
    }
    return {relation, dt_start, dt_end}
  }
  return {
    filterAll: true, // filtering all rows at once
    filterMethod: (filter, rows) => {
      let {relation, dt_start, dt_end, invalid} = decode_filter(filter.value)
      if (invalid) {
        return []
      }
      if (!relation) {
        return rows
      }
      let filtered = rows.filter(r => {
        let row_val = r[filter.id]
        if (row_val === undefined) return false

        let dt_row = row_val || parseISO(0)
        return relation(dt_row, dt_start, dt_end)
      })

      let aggregated = rows.filter(r => r._aggregated)
      return aggregated.length == 0 ? filtered : [...filtered, ...aggregated]
    },
    Filter: textFilter({
      title: 'Relation: >2010-03 or <2010, Range: d1 -> d2',
      validate: v => !decode_filter(v).invalid,
      name: colid,
    }),
  }
}

export const filterMatchSorter = accessor_key => {
  /* filter by substrings
    - with matchSorter: https://github.com/kentcdodds/match-sorter
    - with regexp
  */

  let is_regexp = s => (s.startsWith('/') && s.endsWith('/')) || s.endsWith('/i')
  let get_regexp = s => {
    let i_case = s.endsWith('i')
    s = s.slice(1, i_case ? -2 : -1)
    try {
      return new RegExp(s, i_case ? 'i' : undefined)
    } catch (e) {
      return null
    }
  }
  return {
    filterAll: true, // for giving rows to matchSorter
    filterMethod: (filter, rows) => {
      let f_val = strip(filter.value || '')
      if (!f_val) {
        return rows
      }

      let filtered = []
      if (is_regexp(f_val)) {
        // filter with regexp
        let rx = get_regexp(f_val)
        if (rx) {
          filtered = rows.filter(r => {
            let row_val = get(r, accessor_key)
            if (row_val === undefined) return false
            return rx.test(row_val || '')
          })
        }
      } else {
        // see https://github.com/kentcdodds/match-sorter#threshold-number
        // default is matchSorter.rankings.MATCHES
        filtered = matchSorter(rows, f_val, {
          keys: [accessor_key],
          threshold: matchSorter.rankings.CONTAINS,
        })
      }

      let aggregated = rows.filter(r => r._aggregated)
      return aggregated.length == 0 ? filtered : [...filtered, ...aggregated]
    },
    Filter: textFilter({
      title:
        'Search substrings or use /.*/ for regexp mode e.g. /S/ for non-empty or /^$/ for empty',
      validate: v => !is_regexp(v) || !!get_regexp(v),
      name: accessor_key,
    }),
    // assume regexp
    constructFilter: text => `/${text}/`,
  }
}

export const getNumberFilterFunc = text => {
  let stripped_prefix = ['<', '>', '=', '~'].includes(text?.[0]) ? text.slice(1) : text
  let parsed_val = parseFloat(stripped_prefix)

  if (text && ['kb', 'mb', 'gb'].includes(text.slice(-2).toLowerCase())) {
    let s = text.toLowerCase()
    if (s.endsWith('kb')) {
      parsed_val = parsed_val * 1024
    } else if (s.endsWith('mb')) {
      parsed_val = parsed_val * 1024 ** 2
    } else if (s.endsWith('gb')) {
      parsed_val = parsed_val * 1024 ** 3
    }
  }

  let func = null
  if (text && stripped_prefix) {
    if (text == 'null') {
      func = v => v === null
    } else if (text[0] == '<') {
      func = v => v !== null && v < parsed_val
    } else if (text[0] == '>') {
      func = v => v !== null && v > parsed_val
    } else if (text[0] == '~') {
      // +/-10 %
      func = v => v !== null && v > parsed_val * 0.9 && v < parsed_val * 1.1
    } else if (text[0] == '=' || stripped_prefix) {
      func = v => v == parsed_val
    }
  }
  return func
}

const NumberFilter = ({filter, onChange}) => {
  let [text, setText] = useState(null)
  let [debounced] = useDebounce(text, 300)
  useEffect(() => {
    if (debounced !== null) {
      let func = getNumberFilterFunc(debounced)
      onChange({text: debounced, func, type: NUMBER_TYPE})
    }
  }, [debounced])
  let text_val = text === null ? filter?.value?.text : text

  return (
    <input
      value={text_val || ''}
      type="text"
      onChange={event => setText(event.target.value)}
      className={clsx(styles.colFilter, !!text_val && styles.activeFilter)}
      title={text_val ? '' : "prefix with <, >, ~, = or use the custom value 'null' for filtering"}
    />
  )
}

export const filterNumbers = (opts = {}) => ({
  /* filter numbers with prefix/suffix micro dsl
    opts.transform: transform value before filtering
   */
  filterMethod: (filter, row) => {
    if (row._aggregated) return true

    if (filter.value.func) {
      let row_val = row[filter.id]
      if (opts.transform) {
        row_val = opts.transform(row_val)
      }
      // unloaded values (undefined) should always be filtered
      return row_val !== undefined && filter.value.func(row_val)
    }
    return true
  },
  Filter: NumberFilter, // this cant be a callable here (will lead to reconstruct/unmount)
  constructFilter: text => ({text, func: getNumberFilterFunc(text), type: NUMBER_TYPE}),
})

export const MultiChoicePopup = ({options, values, setValues}) => {
  /**
   * renders a popup with multiple checkbox options.
   * It handles updating an array of selected values upon checking or unchecking an option.
   *
   * `options` (array of option objects)
   * `values` (array of selected value ids),
   * `setValues` (function to update state of selected values).
   */

  values = values.filter(v => v !== CATEGORIAL_ALL)

  let onChange = e => {
    let vs = null
    let {checked, id} = e.target
    if (checked) {
      // If the checkbox is checked, add the id to the values array.
      vs = uniq([...values, id]).filter(Boolean)
    } else {
      // If the checkbox is unchecked, remove the id from the values array.
      vs = uniq(values.filter(value => value !== id)).filter(Boolean)
    }
    setValues(vs)
  }
  return (
    <XPopup
      on="click"
      hoverable
      trigger={<Button basic compact icon="edit" size="small" />}
      position="bottom left"
      positionFixed
    >
      {options.map(o => (
        <div key={o.value}>
          <input
            type="checkbox"
            checked={values.includes(o.value)}
            id={o.value}
            onChange={onChange}
          />
          <label htmlFor={o.value}>{o.label}</label>
        </div>
      ))}
    </XPopup>
  )
}

export const constructOptionFilterValue = (label2value, useTypeAnomaly = false) => text => {
  // gpt input is natural language based, we receive state names here
  // and transform them to state value (ints)
  let filter_res = 'all'
  // sometimes multiple are given as 'X|Y|Z'
  // sometimes directly as a list ['X', 'Y', 'Z']
  if (isString(text) && text.includes('|')) {
    text = text.split('|').map(strip)
  }
  let get_value = e => {
    if (useTypeAnomaly || size(label2value) == 0) {
      // no mapping given - return unmapped (as in type:book)
      return e
    }
    return label2value[e]
  }

  if (isArray(text)) {
    // cc0,cc-by*,cc-by-sa*,cc-by-nc*,cc-by-nd*,cc-by-nc-sa*,cc-by-nc-nd*
    let res = uniq(
      flatten(
        text.map(e => {
          if (e.endsWith('*')) {
            let prefix = e.slice(0, -1)
            return mapItems(label2value, (k, v) => v, k => k.startsWith(prefix))
          } else {
            return get_value(e) ? [get_value(e)] : []
          }
        })
      )
    )
    if (res.length > 0) {
      filter_res = new Set(res)
    }
  } else if (get_value(text)) {
    filter_res = get_value(text)
  }
  return filter_res
}

const CategorialFilter = ({filter, onChange, column}) => {
  let {options, value2label} = column.filterMetaProps
  let value = filter ? filter.value : CATEGORIAL_ALL
  let custom_opt = null
  if (isString(value)) {
    // value = filter.value
  } else if (isSet(value)) {
    if (size(value) == 0) {
      value = CATEGORIAL_ALL
    } else {
      let values = [...value]
      value = values.join(',')
      let count_more = null
      if (values.length > 3) {
        count_more = values.length - 3
        values = values.slice(0, 3)
      }
      custom_opt = (
        <option key="custom" value={value}>
          {values.map(v => value2label[v]).join(',')}
          {count_more && ` [${count_more} more]`}
        </option>
      )
    }
  } else {
    console.warn('Invalid value type %o', value)
    value = ''
  }
  return (
    <span style={{width: '100%', display: 'flex'}}>
      <select
        onChange={event => onChange(event.target.value)}
        value={value}
        style={{flex: 1}}
        className={clsx(styles.colFilter, value !== CATEGORIAL_ALL && styles.activeFilter)}
      >
        <option key="all" value={CATEGORIAL_ALL}>
          All
        </option>
        {custom_opt}
        {options.map(({value: v, label}) => (
          <option key={v} value={v}>
            {label}
          </option>
        ))}
      </select>
      <MultiChoicePopup
        options={options}
        values={value.split(',')}
        setValues={values => onChange(new Set(values))}
      />
    </span>
  )
}

export const filterOptions = (options, useTypeAnomaly = false) => {
  /* filter by categorial keys,
  options: [{value, label}, ...]
  */
  let value2label = dict(options.map(o => [o.value, o.label]))

  return {
    filterMethod: (filter, row) => {
      if (row._aggregated || filter.value === CATEGORIAL_ALL) {
        return true
      }
      let row_val = String(row[filter.id])
      if (isSet(filter.value)) {
        // or relation
        return filter.value.has(row_val)
      } else {
        return row_val !== undefined && String(row_val) === filter.value
      }

      // unloaded values (undefined) should always be filtered
      return row_val !== undefined && String(row_val) === filter.value
    },
    Filter: CategorialFilter,
    // pass through to CategorialFilter (cannot be wrapped in func, will lead to unmounts on rerender)
    filterMetaProps: {options, value2label},
    constructFilter: constructOptionFilterValue(
      dict(options.map(o => [o.label, o.value])),
      useTypeAnomaly
    ),
    categorialLabels: value2label,
  }
}

export const useCategoricalColumn = (rows, column) =>
  /* build categorial column with memoized counted filter values for filter dropdown
    this adds .filterMethod and .Filter
    and derives from .filterLabels function
    */
  useMemo(() => {
    let counts = {}

    let {accessor, filterLabels, knownValues} = column
    let f_access = row => accessor(row)
    if (isString(accessor)) {
      let access = String(accessor)
      f_access = row => String(row[access])
    }

    for (let r of rows) {
      let v = f_access(r)
      if (!has(counts, v)) {
        counts[v] = 1
      } else {
        counts[v] += 1
      }
    }

    if (knownValues) {
      let values = uniq([...knownValues, ...keys(counts)])
      var aggregation = values.map(value => filterLabels({value, count: counts[value] || 0}))
    } else {
      var aggregation = mapItems(counts, (value, count) => filterLabels({value, count}))
    }
    return {...column, ...filterOptions(aggregation)}
  })
